import { useCallback, useRef, useState } from 'react';
import { Slider } from 'antd';
import { Modal } from 'src/components/design-system/modal';
import AvatarEditor, { type AvatarEditorRef } from 'react-avatar-editor';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import styles from './crop-modal.module.scss';

const MODAL_WIDTH = 520;
// Fill the modal body (Ant modal content has 24px padding on each side).
const EDITOR_SIZE = MODAL_WIDTH - 48;

// The backend caps avatar uploads by file size and stores a 1024² square. getImage()
// returns the crop at the source's full resolution — multi-MB for phone photos — which
// trips that cap. The lib has no target-size option (getImageScaledToCanvas only returns
// the on-screen editor size, well below 1024), so we downscale to 1024 ourselves.
const MAX_AVATAR_DIM = 1024;

/**
 * Downscale a square crop so its side is at most `maxDim`, preserving the original when
 * it's already smaller. The crop is always square (the editor is square), so one side
 * describes it. `ratio` is `min(1, maxDim / side)`: the `min(1, …)` caps it at 1 so we
 * never upscale a smaller crop; otherwise it's `maxDim / side`, the shrink factor.
 */
function downscaleToMax(source: HTMLCanvasElement, maxDim: number): HTMLCanvasElement {
  const ratio = Math.min(1, maxDim / source.width);
  if (ratio === 1) return source;
  const scaled = document.createElement('canvas');
  scaled.width = scaled.height = Math.round(source.width * ratio);
  scaled.getContext('2d')?.drawImage(source, 0, 0, scaled.width, scaled.height);
  return scaled;
}

interface CropModalProps {
  open: boolean;
  imageSrc: string | null;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

export function CropModal({ open, imageSrc, onCancel, onConfirm }: CropModalProps) {
  const lang = useSelector(getLang('PROFILE_PHOTO'));
  const generalLang = useSelector(getLang('GENERAL'));
  const editorRef = useRef<AvatarEditorRef | null>(null);
  const [scale, setScale] = useState(1);
  const [saving, setSaving] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!editorRef.current) return;
    setSaving(true);
    try {
      const canvas = downscaleToMax(editorRef.current.getImage(), MAX_AVATAR_DIM);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b: Blob | null) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
          'image/png',
        );
      });
      onConfirm(blob);
    } finally {
      setSaving(false);
    }
  }, [onConfirm]);

  const reset = useCallback(() => {
    setScale(1);
  }, []);

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  return (
    <Modal
      title={lang.CROP_TITLE}
      open={open}
      onCancel={handleCancel}
      onSubmit={handleConfirm}
      cancelText={generalLang.CANCEL}
      submitText={generalLang.APPLY}
      loading={saving}
      submitDisabled={!imageSrc}
      afterClose={reset}
      width={MODAL_WIDTH}>
      <div className={styles.editorWrap}>
        {imageSrc && (
          <AvatarEditor
            ref={editorRef}
            image={imageSrc}
            width={EDITOR_SIZE}
            height={EDITOR_SIZE}
            border={0}
            borderRadius={EDITOR_SIZE / 2}
            color={[0, 0, 0, 0.5]}
            scale={scale}
            rotate={0}
          />
        )}
      </div>
      <div className={styles.control}>
        <span className={styles.controlLabel}>{lang.CROP_ZOOM_LABEL}</span>
        <Slider min={1} max={3} step={0.05} value={scale} onChange={setScale} />
      </div>
    </Modal>
  );
}
